import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CurriculumInfo } from '../services/auth';
import { CurriculumSelectorModal } from '../components/auth/CurriculumSelectorModal';
import '../styles/Auth.css';

export const RegisterPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState<'student' | 'parent'>('student');
  const [curriculum, setCurriculum] = useState('algebra1');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [showCurriculumSelector, setShowCurriculumSelector] = useState(false);
  const [pendingCurricula, setPendingCurricula] = useState<CurriculumInfo[]>([]);
  const { register, googleLogin, appleLogin, switchCurriculum, error, clearError } = useAuth();
  const navigate = useNavigate();

  const validateForm = (): boolean => {
    if (password.length < 6) {
      setValidationError('Password must be at least 6 characters');
      return false;
    }
    if (password !== confirmPassword) {
      setValidationError('Passwords do not match');
      return false;
    }
    setValidationError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    clearError();

    try {
      const result = await register(email, password, name, role, role === 'student' ? curriculum : undefined);
      // Check if user has multiple curricula (existing user adding new grade)
      if (role === 'student' && result.has_multiple_curricula && result.available_curricula.length > 1) {
        setPendingCurricula(result.available_curricula);
        setShowCurriculumSelector(true);
      } else {
        navigate('/verify-email');
      }
    } catch {
      // Error is handled by context
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignup = async () => {
    if (!ageConfirmed) {
      setValidationError('Please confirm the age requirement before signing up.');
      return;
    }
    setIsSubmitting(true);
    clearError();

    try {
      const result = await googleLogin(role, role === 'student' ? curriculum : undefined);
      // Check if user has multiple curricula (existing user adding new grade)
      if (role === 'student' && result.has_multiple_curricula && result.available_curricula.length > 1) {
        setPendingCurricula(result.available_curricula);
        setShowCurriculumSelector(true);
      } else if (role === 'student') {
        navigate('/dashboard');
      } else {
        navigate('/parent/dashboard');
      }
    } catch {
      // Error is handled by context
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAppleSignup = async () => {
    if (!ageConfirmed) {
      setValidationError('Please confirm the age requirement before signing up.');
      return;
    }
    setIsSubmitting(true);
    clearError();

    try {
      const result = await appleLogin(role, role === 'student' ? curriculum : undefined);
      if (role === 'student' && result.has_multiple_curricula && result.available_curricula.length > 1) {
        setPendingCurricula(result.available_curricula);
        setShowCurriculumSelector(true);
      } else if (role === 'student') {
        navigate('/dashboard');
      } else {
        navigate('/parent/dashboard');
      }
    } catch {
      // Error is handled by context
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCurriculumSelect = async (selectedCurriculum: string) => {
    await switchCurriculum(selectedCurriculum);
  };

  const handleCurriculumSelectorClose = () => {
    setShowCurriculumSelector(false);
    navigate('/dashboard');
  };

  const displayError = validationError || error;

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <div className="auth-logo">
            <svg className="auth-logo-icon" viewBox="0 0 64 64" fill="none">
              <rect width="64" height="64" rx="16" fill="#2563EB"/>
              <rect x="12" y="22" width="10" height="20" rx="3" fill="#FACC15"/>
              <rect x="42" y="22" width="10" height="20" rx="3" fill="#FACC15"/>
              <rect x="22" y="28" width="20" height="8" rx="2" fill="white"/>
              <line x1="17" y1="29" x2="17" y2="35" stroke="#1E40AF" strokeWidth="2.5" strokeLinecap="round"/>
              <line x1="14" y1="32" x2="20" y2="32" stroke="#1E40AF" strokeWidth="2.5" strokeLinecap="round"/>
              <line x1="44.5" y1="29.5" x2="49.5" y2="34.5" stroke="#1E40AF" strokeWidth="2.5" strokeLinecap="round"/>
              <line x1="49.5" y1="29.5" x2="44.5" y2="34.5" stroke="#1E40AF" strokeWidth="2.5" strokeLinecap="round"/>
              <circle cx="52" cy="16" r="2.5" fill="#FACC15"/>
            </svg>
            <span className="auth-logo-text">Reps<span>Learn</span></span>
          </div>
          <h1>Create Account</h1>
          <p>Start your math learning journey</p>
        </div>

        {displayError && (
          <div className="auth-error">
            {displayError}
          </div>
        )}

        <div className="role-selector">
          <button
            type="button"
            className={`role-button ${role === 'student' ? 'active' : ''}`}
            onClick={() => setRole('student')}
          >
            I'm a Student
          </button>
          <button
            type="button"
            className={`role-button ${role === 'parent' ? 'active' : ''}`}
            onClick={() => setRole('parent')}
          >
            I'm a Parent
          </button>
        </div>

        {role === 'student' && (
          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <label htmlFor="curriculum">Grade / Curriculum</label>
            <select
              id="curriculum"
              value={curriculum}
              onChange={(e) => setCurriculum(e.target.value)}
              disabled={isSubmitting}
              className="form-select"
            >
              <option value="grade6">Grade 6 Math</option>
              <option value="grade7">Grade 7 Math</option>
              <option value="grade8">Grade 8 Math</option>
              <option value="algebra1">Algebra 1 (Grade 9)</option>
              <option value="math1">Mathematics I (Grade 9)</option>
            </select>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              required
              minLength={6}
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="age-confirmation">
            <label className="age-checkbox-label">
              <input
                type="checkbox"
                checked={ageConfirmed}
                onChange={(e) => setAgeConfirmed(e.target.checked)}
                disabled={isSubmitting}
              />
              <span>I confirm I am at least 13 years old, or I have my parent/guardian's permission to use this service.</span>
            </label>
          </div>

          <button
            type="submit"
            className="auth-button primary"
            disabled={isSubmitting || !email || !password || !name || !ageConfirmed}
          >
            {isSubmitting ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <button
          onClick={handleGoogleSignup}
          className="auth-button google-button"
          disabled={isSubmitting}
        >
          <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign up with Google
        </button>

        <button
          onClick={handleAppleSignup}
          className="auth-button apple-button"
          disabled={isSubmitting}
        >
          <svg className="apple-icon" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
          </svg>
          Sign up with Apple
        </button>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <Link to="/login">Sign in</Link>
          </p>
        </div>

        <a href="https://www.facebook.com/repslearn" target="_blank" rel="noopener noreferrer" className="auth-social-link">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          Follow us on Facebook
        </a>
      </div>

      {showCurriculumSelector && pendingCurricula.length > 0 && (
        <CurriculumSelectorModal
          curricula={pendingCurricula}
          onSelect={handleCurriculumSelect}
          onClose={handleCurriculumSelectorClose}
          showAddGrade={false}
        />
      )}
    </div>
  );
};
